import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, type ApiError } from './client';

/**
 * Your own account: password and sessions.
 *
 * Separate from `/admins`, which is about managing *other* people. These all
 * act on whoever is signed in, so none of them take an id.
 */

export interface SessionRow {
  id: string;
  current: boolean;
  issuedAt: string;
  lastSeenAt: string;
  expiresAt?: string;
  userAgent?: string | null;
}

/** The API's rules (`checkPasswordStrength`), word for word, to tick off while typing. */
export function passwordProblems(password: string): string[] {
  const problems: string[] = [];
  if (password.length < 12) problems.push('Use at least 12 characters');
  if (password.length > 200) problems.push('Use at most 200 characters');
  if (!/[A-Z]/.test(password)) problems.push('Include a capital letter');
  if (!/[0-9]/.test(password)) problems.push('Include a number');
  if (!/[^A-Za-z0-9\s]/.test(password)) problems.push('Include a special character, like ! ? # or @');
  if (/^\s|\s$/.test(password)) problems.push('Remove leading or trailing spaces');
  if (/^(.)\1+$/.test(password)) problems.push('Use more than one distinct character');
  return problems;
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => request<{ sessions: SessionRow[] }>('/auth/sessions'),
    select: (d) => d.sessions,
  });
}

export function useSignOutEverywhereElse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request<{ ok: boolean }>('/auth/sessions', { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

/**
 * Changing a password revokes every session, this one included — the API says
 * so with `reauthRequired`, and the caller signs out rather than leaving a
 * dashboard on screen whose next request will 401. The first password after an
 * invite is the exception: that session carries on (`reauthRequired: false`).
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      request<{ ok: boolean; reauthRequired: boolean }>('/auth/password', {
        method: 'POST',
        body,
      }),
  });
}

/** Unauthenticated: asks for a reset link. */
export function useRequestReset() {
  return useMutation({
    mutationFn: (email: string) =>
      request<{ ok: boolean }>('/auth/password/reset-request', {
        method: 'POST',
        body: { email },
        anonymous: true,
      }),
  });
}

/** Unauthenticated: redeems an invite or reset token. */
export function useRedeemReset() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      request<{ ok: boolean; wasInvite: boolean }>('/auth/password/reset', {
        method: 'POST',
        body,
        anonymous: true,
      }),
  });
}

/**
 * The rules a refused password broke, in the API's words.
 *
 * The API sends them as the error's `details` array — `unprocessable('That
 * password is not strong enough.', problems)` on POST /auth/password and
 * /auth/password/reset. This read `details.problems`, which the API never
 * sent, so a server-side refusal showed only the generic sentence. The object
 * shape is still accepted, in case a route ever wraps the list.
 */
export function problemsFrom(error: unknown): string[] {
  const details = (error as ApiError | undefined)?.details;
  const list = Array.isArray(details) ? details : (details as { problems?: unknown } | undefined)?.problems;
  return Array.isArray(list) ? list.filter((p): p is string => typeof p === 'string') : [];
}
